// Enums
export enum AccountStatus {
  PENDING = 1,
  REVIEWING = 2,
  MISSING = 3,
  ACTIVE = 4,
  BLOCKED = 5,
}

// Wire values from backend OrderStatusEnum (Heglo.InterCommunications
// Enums.cs): the legacy 2/3 slots were retired server-side long ago, so
// comparisons against them silently never matched (dead status UI).
export enum OrderStatus {
  PENDING = 1,
  FINISHED = 7,
  CANCELED = 8,
  WaitingProviders = 9,
  Opening = 10,
}

export enum OrderQuality {
  Pending = 0,
  Under_Processing = 1,
  Completed = 2,
}

// Backend OrderTypeEnum: these are pricing models, not business domains.
export enum OrderType {
  Quotation = 4,
  CatalogService = 5,
  ProviderService = 6,
  CatalogOffer = 7,
  ProviderOffer = 8,
}

export enum ExecutionType {
  Service = 1,
  Inspection = 2,
  PricedService = 3,
}

// Wire values from backend OrderInnerStatusEnum: MOVING=4, REACHED=5,
// STARTING=6, Scheduled=7. There is no inner "completed" — completion is a
// full status change to FINISHED through the order-status endpoint.
export enum InnerOrderStatus {
  None = 0,
  OnTheWay = 4,
  Arrived = 5,
  Started = 6,
  Scheduled = 7,
}

export enum GuaranteeStatus {
  None = 0,
  Active = 1,
  Ended = 2,
}

// Must match backend SupportCombinedQueryEnum exactly
export enum SupportCombinedQuery {
  // Primary statuses
  Review = 1,
  Pending = 2, // WaitingProviders in backend context
  // Opening sub-filters
  Opening_All = 3,
  Opening_No_Schedule = 4,
  Opening_Schedule = 5,
  Opening_Late = 6,
  Opening_Normal = 7,
  Opening_UpNormal = 8,
  Opening_Danger = 9,
  // Completed sub-filters
  Completed_All = 10,
  Completed_Agree = 11,
  Completed_Not_Agree = 12,
  // Quality sub-filters
  Quality_All = 13,
  Quality_Pending = 14,
  Quality_Under_Processing = 15,
  Quality_Completed = 16,
  // Canceled sub-filters
  Canceled_All = 17,
  Canceled_Platform = 18,
  Canceled_Customer = 19,
  // All orders
  All = 20,
}

export enum UserType {
  Customer = 1,
  Provider = 2,
  Support = 3,
}

/** Backend UpdateProfileTypeEnum — selects which section of
 *  ProviderProfileInputModel a /supports/providers/profile POST writes. */
export enum ProviderProfileUpdateType {
  /** Legacy full-rebuild path — avoid: nulls untouched personal fields. */
  Old = 0,
  PersonalProfile = 1,
  CenterInfo = 2,
  ShopInfo = 3,
  ProviderConfig = 4,
}

/** Backend NotificationMethodType — delivery channel for notifications. */
export enum NotificationMethodType {
  None = 0,
  SMS = 1,
  Push = 2,
  SMSPush = 3,
  Email = 4,
}

export enum NotificationAudience {
  All = 0,
  Customers = 1,
  Providers = 2,
}

export enum NotificationBroadcastScope {
  Tenant = 0,
  Global = 1,
}

/** Backend NotificationTypeEnum — only the values the dashboard sends. */
export enum NotificationType {
  DirectManually = 16,
  /** Broadcast to an FCM topic (CAST). */
  Cast = 27,
}

/** Backend NotificationUpdateType — read-receipt status values. */
export enum NotificationUpdateType {
  Seen = 1,
  Read = 2,
}

/** Backend CenterOpenningDays legacy int encoding (NOT a day list). */
export enum CenterOpeningDaysOption {
  AllWeek = 1,
  AllExceptFriday = 2,
  AllExceptFridaySaturday = 3,
}

export enum AttachmentType {
  ID = 1,
  License = 2,
  Certificate = 3,
  Other = 4,
}

export enum AttachmentStatus {
  Pending = 1,
  Approved = 2,
  Rejected = 3,
}

export enum TicketStatus {
  Open = 1,
  InProgress = 2,
  Resolved = 3,
  Closed = 4,
}

export enum CouponStatus {
  Active = 1,
  Used = 2,
  Expired = 3,
  Invalid = 4,
}

export enum OfferStatus {
  Pending = 1,
  Active = 2,
  Removed = 3,
  Expired = 5,
}

export enum MessageType {
  Text = 1,
  Image = 2,
  Audio = 3,
  Video = 4,
  Document = 5,
}

export enum TicketInputStatus {
  Open = 1,
  Closed = 2,
  All = 3,
}

export enum UserNoteType {
  General = 1,
  Violations = 2,
  Penalties = 3,
  CustomerNotes = 4,
  Contracts = 5,
}

export enum UserNoteDepartment {
  General = 1,
  Providers = 2,
  Customers = 3,
}

// API Response Types
export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ApiError {
  message: string;
  errors?: string[];
}

// Auth Types
export interface LoginRequest {
  mobile: string;
  clientType: string;
}

export interface TokenRequest {
  Mobile: string;
  OTP: string;
  ClientType: string;
  GrantType?: string;
  RefreshToken?: string;
}

export interface TokenResponse {
  tokenString: string;
  expiration: string;
  refreshToken: string;
}

export interface SupportProfile {
  id: number;
  userId: string;
  fullName: string;
  mobileNo: string;
  profileImage: string;
  genderId: number;
  birthDate?: string;
  accountStatusId: number;
}

// Dashboard Types
export interface DashboardViewModel {
  newCustomers: number;
  newProviders: number;
  smsPendingCustomers: number;
  smsPendingProviders: number;
  notVerifiedProviders: number;
  verifiedProviders: number;
  ordersCount: number;
  pendingOrders: number;
  openingOrders: number;
  canceledOrders: number;
  completedOrders: number;
  offers: number;
  ordersCanceledBySupport: number;
  ordersCanceledByUser: number;
  todayProviders: number;
  todayCustomers: number;
  todayActiveProviders: number;
  todayLocations: number;
  specialtyCoverageSubscriptionsCount: number;
}

// Customer Types (matches backend CustomerViewModel)
export interface Customer {
  id: number;
  userId: string;
  fullName: string;
  mobileNo: string;
  profileImage: string;
  genderId: number;
  genderArabic?: string;
  gender?: string; // Display name for gender
  birthDate?: string;
  email?: string;
  // Location fields
  cityId?: number;
  city?: string;
  nationalityId?: number;
  nationality?: string;
  // Status fields (backend uses CustomerStatusId, not accountStatusId)
  customerStatusId: number;
  customerStatusArabic?: string;
  customerVerificationMessage?: string;
  supportStatusMessage?: string;
  supportChangeStatusOn?: string;
  supportChangeStatusOnString?: string;
  // Registration & usage
  registerOn: string;
  registerOnString?: string;
  lastTimeUseTheApp?: string;
  lastTimeUseTheAppString?: string;
  versionCode?: number;
  // Credits
  deepLinkCredits: number;
  appCredits: number;
  loyaltyOrdersCounter?: number;
  // Referral fields
  deepLink?: string;
  referralUserId?: string;
  referralUserOn?: string;
  referralUserName?: string;
  // Rating (from provider ratings of this customer)
  rate?: number;
  rateCount?: number;
  // Account type
  accountType?: number;
  accountClassTypeId?: number;
  companyDetials?: string;
  discoverySource?: string;
  // App rating
  appRatedOn?: string;
  appRatedOnString?: string;
  // Onboarding
  onBoardingStatus?: number;
  onBoardingUpdatedOn?: string;
  onBoardingUpdatedOnString?: string;
  // Backward compatibility alias
  accountStatusId?: number;
  // Stats
  ordersCount?: number;
}

export interface FullCustomerViewModel {
  customerViewModel: Customer;
  userStatisticViewModel: UserStatistics;
}

// Provider Types
export interface Provider {
  id: number;
  parentProviderId?: number | null;
  parentProviderName?: string | null;
  userId: string;
  fullName: string;
  mobileNo: string;
  profileImage: string;
  providerClassTypeId?: number;
  maxSpecialtiesCount?: number;
  maxOpenedOrdersCount?: number;
  genderId: number;
  gender?: string;
  genderArabic?: string;
  genderEnglish?: string;
  birthDate?: string;
  nationalityId?: number;
  nationality?: string;
  nationalityArabic?: string;
  nationalityEnglish?: string;
  cityId?: number;
  city?: string;
  cityArabic?: string;
  cityEnglish?: string;
  providerStatusId: number;
  providerStatus?: string;
  providerVerificationMessage?: string;
  registerOn: string;
  lastTimeUseTheApp?: string;
  versionCode?: number;
  deepLinkCredits: number;
  appCredits: number;
  // Referral fields
  deepLink?: string;
  referralUserId?: string;
  referralUserName?: string;
  rate: number;
  rateCount: number;
  providerTypeId?: number;
  providerType?: string;
  commercialName?: string;
  commercialRegistration?: string;
  bio?: string;
  temporarySuspensionTo?: string;
  isSuspension: boolean;
  specialities: ProviderSpecialty[];
  lastLatitude?: number;
  lastLongitude?: number;
  lastUpdatedLocation?: string;
  lastLocationImage?: string;
  distanceFromUser?: number;
  isOnline?: boolean;
  isOnlineUpdateOn?: string;
  // Stats
  ordersCount?: number;
  mobileNo2?: string | null;
  birthDateString?: string;
  businessName?: string | null;
  businessDescription?: string | null;
  businessSegment?: string | null;
  haveCenter?: boolean;
  centerName?: string;
  centerDescription?: string;
  centerLocationDescription?: string;
  centerLatitude?: number;
  centerLongitude?: number;
  centerOpenAt?: string;
  centerCloseAt?: string;
  centerOpenningDays?: CenterOpeningDaysOption;
  centerSpecailtyId?: number;
  centerLogoImage?: string | null;
  centerCoverImage?: string | null;
  haveStore?: boolean;
  shopName?: string;
  shopDescription?: string;
  shopLocationDescription?: string;
  shopLatitude?: number;
  shopLongitude?: number;
  shopSpecailtyId?: number;
  shopLogoImage?: string | null;
  shopCoverImage?: string | null;
  isShopHaveDelivery?: boolean;
  deliveryPriceNear?: number;
  deliveryPriceFar?: number;
  freeDeliveryThreshold?: number | null;
}

export interface FullProviderViewModel {
  providerViewModel: Provider;
  userStatisticViewModel: UserStatistics;
}

export interface ProviderSpecialty {
  useConfiguredAvailability?: boolean;
  id: number;
  specialtyId: number;
  specialtyArabic: string;
  specialtyEnglish: string;
  specialityName?: string;
  specialtyIcon?: string;
  icon?: string;
  arabicName?: string;
  englishName?: string;
  specialtyArabicName?: string;
  specialtyEnglishName?: string;
  isActive: boolean;
  googlePlayPublishingMode: 'heglo-managed' | 'tenant-managed';
  categoryArabic?: string;
  specialtyType?: SpecialtyTypeEnum;
  ordersCount?: number;
  completedOrders?: number;
  canceledOrders?: number;
  rate?: number;
  subSpecialties?: SubSpecialty[];
}

// Phase 1: Team & Dispatch types
export interface TeamMember {
  id: number;
  userId: string;
  fullName: string;
  mobileNo?: string;
  profileImage?: string;
  workerRole?: number; // 0=ServiceWorker, 1=Driver, 2=ServiceAndDriver
  isActive: boolean;
  isOnline: boolean;
  activeTasksCount?: number;
  completedTasksToday?: number;
  latitude?: number;
  longitude?: number;
  rate?: number;
  vehicleType?: string;
  vehiclePlate?: string;
  specialtyIds?: number[];
  zoneNames?: string[];
  workerRoleText?: string;
  statusText?: string;
}

export type TeamMemberViewModel = TeamMember;

export interface CustomQueryParameters {
  page?: number;
  pageSize?: number;
  userId?: string;
  specialtyId?: number;
  forDate?: string;
  [key: string]: string | number | boolean | undefined | null;
}

export type OperatingMode = 0 | 1 | 2; // 0=Single, 1=Company, 2=WorkingName
export type WorkerRole = 0 | 1 | 2; // 0=ServiceWorker, 1=Driver, 2=ServiceAndDriver

// User Statistics
export interface UserStatistics {
  totalOrders: number;
  completedOrders: number;
  canceledOrders: number;
  openingOrders: number;
  pendingOrders: number;
  waitingOfferOrders?: number;
  totalEarning?: number;
  totalTransactions?: number;
  balance: number;
  consumedBalance: number;
  addedBalance: number;
  credits: number;
  appCredits: number;
  usedCoupons: number;
  couponsPrice: number;
  ratesCount: number;
  rates: number;
  galleriesCount?: number;
  specialitiesCount?: number;
  offersCount?: number;
  servicesCount?: number;
  orderOffersCount?: number;
  orderOffersAcceptedCount?: number;
  orderOffersRejectedCount?: number;
  completedOrdersGood: number;
  completedOrdersBad: number;
  completedOrdersComplains: number;
  ticketsCount: number;
  underQualityReviewOrders: number;
  underDangerZoneOrders?: number;
  needUpdate: boolean;
  haveIssue?: boolean;
  issueSummary?: string;
  isProviderExceedBalance?: boolean;
  isSuspension?: boolean;
  haveNoOrNotUpdatedLocation?: boolean;
  specialtyCoverageSubscriptionsCount?: number;
}

// Order Types
export interface Order {
  id: number;
  orderCode: string;
  customerId: number;
  customerUserId: string;
  customerName: string;
  customerMobile: string;
  customerImage?: string;
  customerProfileImage?: string;
  providerId?: number;
  providerUserId?: string;
  providerName?: string;
  providerMobile?: string;
  providerImage?: string;
  providerProfileImage?: string;
  specialityId: number;
  specialityName?: string;
  specialityArabic: string;
  specialityEnglish: string;
  specialtyType?: number;
  specialtyTypeString?: string;
  orderTypeId: number;
  isCustomerAgreeThePrice?: boolean;
  orderTypeArabic?: string;
  orderStatusId: number;
  orderStatus: string;
  orderStatusArabic?: string;
  innerOrderStatusId?: number;
  orderInnerStatus?: string;
  innerOrderStatusString?: string;
  innerOrderStatusUpdatedOn?: string;
  innerOrderStatusUpdatedOnString?: string;
  orderStatusUpdatedOn?: string;
  orderStatusUpdatedOnString?: string;
  moduleKey?: string;
  orderSourceId?: number;
  assignedWorkerId?: number;
  assignedWorkerName?: string | null;
  branchId?: number | null;
  branchName?: string | null;
  branchAddress?: string | null;
  assignedWorkerUserId?: string;
  assignedDriverId?: number;
  assignedDriverUserId?: string;
  /** #6 — null = pending; set = worker accepted. */
  workerAcceptedOn?: string;
  /** #6 — set = worker rejected; AssignedWorkerId is also cleared. */
  workerRejectedOn?: string;
  workerAssignmentRejectionReason?: string;
  orderQualityId?: number;
  orderQuality?: string;
  orderQualityMessage?: string;
  orderQualityOn?: string;
  orderQualityOnString?: string;
  orderQualitySupportUserId?: string;
  body: string;
  price: number;
  initialPrice: number;
  guaranteePrice: number;
  transportationPrice: number;
  guaranteePeriodInDays?: number;
  guaranteeEndOn?: string;
  guaranteeEndOnString?: string;
  guaranteeStatusEnum?: number;
  coupon?: string;
  couponCode?: string;
  couponDiscount: number;
  couponDiscoun?: number;
  customerBalanceDiscount: number;
  consumeBalance?: boolean;
  executionTypeEnum?: number;
  isRated: boolean;
  rate: number;
  rateComment?: string;
  rateOn?: string;
  rateOnString?: string;
  behaviorRate?: number;
  isCustomerRated: boolean;
  customerRate: number;
  customerRateComment?: string;
  customerRateOn?: string;
  customerRateOnString?: string;
  isOnDangerZone: boolean;
  dangerZoneNote?: string;
  dangerZoneOn?: string;
  dangerZoneOnString?: string;
  createdOn: string;
  createdOnString?: string;
  desireOn?: string;
  desireOnString?: string;
  selectedTime?: string;
  acceptFlexibleTime?: boolean;
  agreedTime?: string;
  agreedTimeOn?: string;
  agreedTimeOnString?: string;
  finishedOn?: string;
  canceledOn?: string;
  cancelReason?: string;
  cancelByUserId?: string;
  closedBody?: string;
  notesCount?: number;
  chatsCount?: number;
  spearItemsTotal?: number;
  orderAddressViewModel: OrderAddress;
  orderOfferStatistics?: OrderOfferStatistics;
  orderImages?: OrderImage[];
  orderItems?: OrderItem[];
  orderAnswers?: SpecialtyQuestionAnswer[];
}

export interface OrderOfferStatistics {
  sendToCount: number;
  manaualOffersCount: number;
  acceptedOffersCount: number;
  rejectedOffersCount: number;
  pendingOffersCount: number;
  averagePrice: number;
}

export interface OrderImage {
  id: number;
  orderId: number;
  imagePath: string;
}

export interface OrderItem {
  id: number;
  orderId: number;
  itemId: number;
  itemName: string;
  quantity: number;
  price: number;
}

export interface OrderAddress {
  latitude: number;
  longitude: number;
  addressName?: string;
  addressDescription?: string;
  districtName?: string;
  cityName?: string;
  district?: {
    arabicName?: string;
    englishName?: string;
    cityArabicName?: string;
    cityEnglishName?: string;
  };
  streetName?: string;
  buildingNo?: string;
  floorNo?: string;
  apartmentNo?: string;
}

export interface OrderOffer {
  id: number;
  orderId: number;
  providerUserId: string;
  providerId?: number;
  providerName: string;
  providerProfileImage?: string;
  providerRate?: number;
  providerTypeId?: number;
  providerType?: string;
  completedOrders?: number;
  openingOrders?: number;
  isOffered: boolean;
  isAccepted: boolean;
  isWillingToWork?: boolean;
  price?: number;
  executionTypeEnum?: number;
  executionType?: string;
  guaranteeDays?: number;
  note?: string;
  offeredOn?: string;
  offeredOnString?: string;
  executionDate?: string;
  executionTime?: string;
  providerLatitude?: number;
  providerLongitude?: number;
  distanceFromOrder?: number;
  assignmentType?: number; // 1=Manual, 2=Automatic
}

export interface OrderNote {
  id: number;
  orderId: number;
  note: string;
  createdByUserId: string;
  createdByName?: string;
  createdOn: string;
}

export interface OrderAction {
  id: number;
  orderId: number;
  actionTypeId: number;
  actionType: string;
  actionByUserId: string;
  actionByName?: string;
  actionByUserImage?: string;
  actionByUserType?: UserType;
  actionOn: string;
  actionOnString?: string;
  note?: string;
}

export interface OrderChat {
  id: number;
  orderId: number;
  userId: string;
  userName?: string;
  userProfileImage?: string;
  userTypeEnum: UserType;
  messageType: MessageType;
  body: string;
  filePath?: string;
  createdOn: string;
  isHandled: boolean;
}

export interface OrderPurchase {
  id: number;
  orderId: number;
  totalPrice: number;
  note?: string;
  createdOn: string;
  items: OrderPurchaseItem[];
  attachments: OrderPurchaseAttachment[];
}

export interface OrderPurchaseItem {
  id: number;
  name: string;
  quantity: number;
  price: number;
}

export interface OrderPurchaseAttachment {
  id: number;
  filePath: string;
}

export interface ConsultationSession {
  id: number;
  orderId: number;
  sessionMode: number;
  sessionModeName?: string;
  status: number;
  statusName?: string;
  fixedDurationMinutes: number;
  gracePeriodMinutes: number;
  startedOn?: string;
  endedOn?: string;
  reconnectDeadline?: string;
  remainingSeconds?: number;
  externalFallbackUsed?: boolean;
  externalFallbackNotes?: string;
  endReason?: number;
  endReasonName?: string;
  customerJoinedOn?: string;
  providerJoinedOn?: string;
  lastHeartbeatOn?: string;
}

// Attachment Types (matches backend AttachmentDetailViewModel)
export interface Attachment {
  id: number;
  userId: string;
  providerId: number;
  imagePath: string; // Backend sends imagePath, not filePath
  attachmentTypeId: number;
  attachmentName: string; // Backend sends attachmentName, not typeName
  createdOn: string;
  createdOnString: string;
  isAccepted: boolean; // Backend sends boolean, not statusId
  actionByUserId?: string;
  adminMessage?: string; // Backend sends adminMessage, not rejectionReason
  actionOn?: string;
  actionOnString?: string;
}

// Location Types
export interface Location {
  id: number;
  userId: string;
  latitude: number;
  longitude: number;
  specialityId: number;
  specialtyName?: string;
  updateOn: string;
  updatedOnString?: string;
  locationMapImageString?: string;
}

// Transaction Types
export interface BalanceTransaction {
  id: number;
  userId: string;
  amount: number;
  typeId: number;
  typeName: string;
  channelId?: number;
  channelName?: string;
  note?: string;
  addedByUserId?: string;
  addedByName?: string;
  orderId?: number;
  createdOn: string;
  expiredOn?: string;
}

// User Note Types
export interface UserNote {
  id: number;
  userId: string;
  note: string;
  userNoteType: UserNoteType;
  userNoteTypeName: string;
  userNoteDepartment: UserNoteDepartment;
  userNoteDepartmentName: string;
  createdByUserId: string;
  createdByName?: string;
  createdOn: string;
  suspetionDays?: number;
}

// Ticket Types
export interface Ticket {
  id: number;
  userId: string;
  userName?: string;
  userProfileImage?: string;
  userTypeEnum: UserType;
  categoryId: number;
  categoryName: string;
  categoryEnglishName?: string;
  subCategoryId?: number;
  subCategoryName?: string;
  subCategoryEnglishName?: string;
  subject: string;
  statusId: TicketStatus;
  statusName: string;
  createdOn: string;
  resolvedOn?: string;
  resolvedByUserId?: string;
  resolvedByName?: string;
  // Agent assignment fields
  currentAgentUserId?: string;
  currentAgentName?: string;
  currentAgentPhoto?: string;
  isLiveSession?: boolean;
  priority?: number;
  lastMessageAt?: string;
  hasUnreadAgentMessages?: boolean;
  // SLA fields
  firstResponseAt?: string;
  slaResponseMinutes?: number;
  slaResolutionMinutes?: number;
  isSlaBreach?: boolean;
}

export interface TicketMessage {
  id: number;
  ticketId: number;
  userId: string;
  userName?: string;
  userProfileImage?: string;
  userTypeEnum: UserType;
  messageType: MessageType;
  body: string;
  filePath?: string;
  createdOn: string;
}

// Coupon Types (from backend CouponListItemViewModel)
export interface Coupon {
  id: number;
  code: string;
  title: string;
  discountAmount: number;
  expiresOn: string;
  expiresOnString: string;
  usageCount: number;
  isActive: boolean;
  couponScope: number;
  couponScopeTargetName?: string;
}

// Coupon details (from backend CouponDetailViewModel)
export interface CouponDetail {
  id: number;
  title: string;
  couponTypeId: number;
  couponTypeName: string;
  firstUsingCount: number;
  reusingCount: number;
  code: string;
  discountAmount: number;
  description: string;
  addedByUserId?: string;
  createdOn: string;
  createdOnString: string;
  activatedOn: string;
  activatedOnString: string;
  expiresOn: string;
  expiresOnString: string;
  usageCount: number;
  couponScopeTargetId: number;
  couponScopeTargetName?: string;
  couponScope: number;
  isPublicListing: boolean;
}

// Offer Types
export interface Offer {
  id: number;
  providerUserId: string;
  providerName?: string;
  specialtyId: number;
  specialtyName?: string;
  title: string;
  description: string;
  price: number;
  discountPrice?: number;
  imageUrl?: string;
  statusId: OfferStatus;
  statusName: string;
  fromTime: string;
  toTime: string;
  createdOn: string;
}

// Gallery Types
export interface Gallery {
  id: number;
  providerUserId: string;
  specialtyId: number;
  title: string;
  description?: string;
  imageUrl: string;
  videoUrl?: string;
  isActive: boolean;
  createdOn: string;
  /** CUX-032 — 0 General, 1 Before, 2 After, 3 WorkInProgress. */
  category?: number;
}

// Service Types
export interface ServiceBookingSettings {
  bufferMinutes: number;
  bookingTimeUnit: number;
  requiredResourceIds: number[];
  depositPercent?: number | null;
  depositFixedAmount?: number | null;
  securityDepositAmount?: number | null;
}

export interface Service {
  bookingSettings?: ServiceBookingSettings;
  id: number;
  specialtyId: number;
  providerUserId: string;
  subSpecialtyId: number;
  subSpecialtyName?: string;
  name: string;
  description?: string;
  price: number;
  orderPriority: number;
  minimumQuantity: number;
  maximumQuantity: number;
  durationMinutes?: number;
  isActive: boolean;
  /** CUX-033 — provider-uploaded photo. Empty / null when not set. */
  imagePath?: string | null;
}

// Product Types (Shop module)
export interface Product {
  id: number;
  specialtyId: number;
  specialtyName?: string;
  subSpecialtyId: number;
  subSpecialtyName?: string;
  providerUserId: string;
  providerId: number;
  providerName?: string;
  name: string;
  description?: string;
  price: number;
  imagePath?: string;
  stockQuantity: number;
  isActive: boolean;
  createdOn: string;
}

// Tenant-wide product counts for the dashboard stat cards
export interface ProductsSummary {
  total: number;
  active: number;
  inactive: number;
  lowStock: number;
  outOfStock: number;
}

export interface ProductInput {
  specialtyId: number;
  subSpecialtyId: number;
  providerUserId: string;
  providerId: number;
  name: string;
  description?: string;
  price: number;
  imagePath?: string;
  stockQuantity: number;
  isActive?: boolean;
}

// Booking Slot Types (Booking module)
export interface BookingSlot {
  id: number;
  providerUserId: string;
  providerId: number;
  providerName?: string;
  specialtyId: number;
  specialtyName?: string;
  dayOfWeek: number;
  dayOfWeekName: string;
  startTime: string;
  endTime: string;
  startTimeFormatted: string;
  endTimeFormatted: string;
  slotDurationMinutes: number;
  maxBookingsPerSlot: number;
  useSharedCapacity?: boolean;
  isActive: boolean;
  createdOn: string;
}

export interface BookingSlotInput {
  specialtyId: number;
  providerUserId: string;
  providerId: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  slotDurationMinutes: number;
  maxBookingsPerSlot: number;
  useSharedCapacity?: boolean;
  isActive?: boolean;
}

export interface AvailableTimeSlot {
  startTime: string;
  endTime: string;
  startTimeFormatted: string;
  endTimeFormatted: string;
  remainingBookings: number;
  isAvailable: boolean;
}

// Address Types
export interface Address {
  id: number;
  userId: string;
  addressName: string;
  latitude: number;
  longitude: number;
  cityId?: number;
  cityName?: string;
  districtId?: number;
  districtName?: string;
  streetName?: string;
  buildingNo?: string;
  floorTypeId?: number;
  floorTypeName?: string;
  apartmentNo?: string;
  addressTypeId?: number;
  addressTypeName?: string;
  isDefault: boolean;
  userTypeEnum?: UserType;
}

// Notification Types
export interface Notification {
  id: number;
  userId: string;
  title: string;
  body: string;
  isRead: boolean;
  createdOn: string;
  data?: Record<string, unknown>;
}

export interface BroadcastNotification {
  id: number;
  title: string;
  message: string;
  topic: string;
  createdOn: string;
  userTypeEnum: number;
  seenCount: number;
  uniqueSeenCount: number;
  readCount: number;
  uniqueReadCount: number;
  addedBySupportUserId?: string;
  addedBySupportUser?: { fullName: string };
  imageLink?: string;
  externalLink?: string;
}

// Lookup Types
export interface Gender {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface Nationality {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface Country {
  id: number;
  arabicName: string;
  englishName: string;
  isoCode: string;
  callingCode: string;
  currencyCode: string;
  defaultTimezone: string;
  isActive: boolean;
  cityCount?: number;
}

export interface City {
  id: number;
  countryId: number;
  arabicName: string;
  englishName: string;
  isActive: boolean;
  districtCount?: number;
}

export interface District {
  id: number;
  cityId: number;
  arabicName: string;
  englishName: string;
}

// CategoryStatusEnum matches backend
export enum CategoryStatusEnum {
  Active = 1,
  Soon = 2,
  Disabled = 3,
}

// Category matches CategoryAdminViewModel
export interface Category {
  id: number;
  arabicName: string;
  englishName: string;
  description?: string;
  imagePath?: string;
  orderPriority: number;
  categoryStatus: CategoryStatusEnum;
  // Legacy field for backward compatibility
  icon?: string;
}

// Specialty matches SpecialtyAdminViewModel (list view)
export interface Specialty {
  id: number;
  categoryId: number;
  categoryName?: string;
  subCategoryId?: number;
  arabicName: string;
  englishName: string;
  imagePath?: string;
  isActive: boolean;
  orderPriority: number;
  specialtyType: SpecialtyTypeEnum;
  specialtyExecuationModel: SpecialtyExecuationModelEnum;
  platformCommission: number;
  createdOn?: string;
  // Legacy fields for backward compatibility
  icon?: string;
  categoryArabic?: string;
  categoryEnglish?: string;
}

export interface SubSpecialty {
  id: number;
  specialtyId: number;
  specialtyName?: string;
  subSpecialtyId?: number;
  subSpecialtyArabic?: string;
  subSpecialtyEnglish?: string;
  arabicName: string;
  englishName: string;
  isActive: boolean;
  orderPriority: number;
  subSpecialtyBy: SubSpecialtyByEnum;
  providerUserId?: string;
  providerId: number;
  createdOn?: string;
}

export interface DispatcherPrice {
  id: number;
  specialtyId: number;
  cityId: number;
  cityArabicName?: string;
  cityEnglishName?: string;
  price: number;
  isActive: boolean;
  createdOn?: string;
  updatedOn?: string;
}

export interface AppVersion {
  id: number;
  versionCode: number;
  versionName: string;
  userType: UserType;
  userTypeId: number;
  isRequired: boolean;
  isForcedUpdate: boolean;
  description?: string;
  releaseNotes?: string;
  createdOn: string;
  updateUrl?: string | null;
}

// Additional Lookup Types
export interface ProviderType {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface FloorType {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface AddressType {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface BalanceTransactionType {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface BalanceTransactionChannel {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface PlatformOffice {
  id: number;
  arabicName: string;
  englishName: string;
  address?: string;
  phone?: string;
}

export interface ViolationType {
  id: number;
  arabicName: string;
  englishName: string;
}

export interface PenaltyType {
  id: number;
  arabicName: string;
  englishName: string;
  suspensionDays?: number;
}

export interface TicketCategory {
  id: number;
  arabicName: string;
  englishName: string;
  userType: UserType;
}

export interface TicketSubCategory {
  id: number;
  categoryId: number;
  arabicName: string;
  englishName: string;
}

// Specialty Question Types
// Backend SpecialtyQuestionForType (Enums.cs): 1-based.
export enum SpecialtyQuestionForType {
  CustomerNewOrder = 1,
  ProviderNewRegistration = 2,
  ProviderOrderCompleteTechnicalReport = 3,
}

// Backend SpecialtyQuestionControlType (Enums.cs): 0-based.
export enum SpecialtyQuestionControlType {
  FreeText = 0,
  SingleChoice = 1,
  MultipleChoice = 2,
  GPS = 3,
  Image = 4,
  FreeTextLong = 5,
  STARS = 6,
}

// Matches backend SpecialtyQuestionViewModel (the real GET shape —
// the old questionArabic/questionType/options shape was wrong and
// the tab pointed at a 404 route; both fixed).
export interface SpecialtyQuestion {
  id: number;
  specialtyId: number;
  specialtyQuestionForType: SpecialtyQuestionForType;
  specialtyQuestionControlType: SpecialtyQuestionControlType;
  isRequired: boolean;
  isActive: boolean;
  createdOn: string;
  createdOnString?: string;
  arabicQuestion?: string;
  englishQuestion?: string;
  // Choice options are a single separated string (AnswerSeparator).
  arabicAnswer?: string;
  englishAnswer?: string;
  answerSeparator?: string;
}

// POST/PUT body — backend SpecialtyQuestionInputModel.
export interface SpecialtyQuestionInput {
  id: number;
  specialtyId: number;
  specialtyQuestionForType: SpecialtyQuestionForType;
  specialtyQuestionControlType: SpecialtyQuestionControlType;
  isRequired: boolean;
  isActive: boolean;
  arabicQuestion?: string;
  englishQuestion?: string;
  arabicAnswer?: string;
  englishAnswer?: string;
  answerSeparator?: string;
}

export interface SpecialtyQuestionOption {
  id: number;
  questionId: number;
  optionArabic: string;
  optionEnglish: string;
}

export interface SpecialtyQuestionAnswer {
  id: number;
  assetProviderId?: number | null;
  arabicAnswer?: string;
  englishAnswer?: string;
  createdOn: string;
  specialtyQuestionId: number;
  specialtyQuestion?: SpecialtyQuestion;
  itemId: number;
  // Legacy fields for order answers
  questionId?: number;
  orderId?: number;
  answer?: string;
}

// Dynamic Config Types
export interface DynamicConfig {
  id: number;
  phoneNumber: string;
  officeTiming: string;
  officeLocationDescription: string;
  officeLocationMapImage: string;
  officeLocationLatitude: number;
  officeLocationLongitude: number;
  facebookLink: string;
  twitterLink: string;
  whatsupLink: string;
  instegramLink: string;
  youtubeLink: string;
}

// Lookups Response
export interface LookupsData {
  genders: Gender[];
  nationalities: Nationality[];
  cities: City[];
  categories: Category[];
  specialties: Specialty[];
  providerTypes: ProviderType[];
  floorTypes: FloorType[];
  addressTypes: AddressType[];
  balanceTransactionTypes: BalanceTransactionType[];
  balanceTransactionChannels: BalanceTransactionChannel[];
  violationTypes: ViolationType[];
  penaltyTypes: PenaltyType[];
  ticketCategories: TicketCategory[];
}

// Admin Auth Types
export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface AdminForgotPasswordRequest {
  email: string;
}

export interface AdminResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
}

export interface AdminProfile {
  id: string;
  userId: string;
  email: string;
  fullName: string;
  mobileNo: string;
  tenantId: number | null;
}

export interface AdminLoginResponse {
  tokenString: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    tenantId: number | null;
    fullName?: string;
    mobileNo?: string;
  };
}

// Subscription Types
export enum SubscriptionStatus {
  Active = 1,
  Expired = 2,
  Suspended = 3,
  Cancelled = 4,
}

export interface Subscription {
  id: number;
  tenantId: number;
  tenantName?: string;
  moduleKeys: string;
  moduleKeysList: string[];
  startDate: string;
  expiryDate?: string;
  isPerpetual: boolean;
  isExpired: boolean;
  status: SubscriptionStatus;
  statusName?: string;
  notes?: string;
  licenseCost: number;
  createdOn: string;
  updatedOn?: string;
}

export interface SubscriptionHistory {
  id: number;
  subscriptionId: number;
  tenantId: number;
  action: string;
  previousModules?: string;
  newModules?: string;
  previousExpiry?: string;
  newExpiry?: string;
  statsSnapshotJson?: string;
  notes?: string;
  performedByUserId?: string;
  createdOn: string;
}

export interface CreateSubscriptionRequest {
  tenantId: number;
  moduleKeys: string[];
  startDate?: string;
  expiryDate?: string;
  notes?: string;
  licenseCost?: number;
}

export interface RenewSubscriptionRequest {
  moduleKeys: string[];
  expiryDate?: string;
  notes?: string;
  licenseCost?: number;
}

// Tenant Types
export interface Tenant {
  id: number;
  name: string;
  identifier: string;
  timezone: string;
  currency: string;
  defaultLocale: string;
  brandingJson?: string;
  isActive: boolean;
  /** Phase 3.HH — block FINISHED transition until provider uploads a completion photo. */
  requireCompletionPhoto?: boolean;
  adminEmail?: string;
  adminPassword?: string;
  adminMobile?: string;
  country?: string;
  city?: string;
  phonePrefix?: string;
  countryCode?: string;
  createdOn: string;
  updatedOn?: string;
  modules: TenantModule[];
  operatingModel: TenantOperatingModel;
  // Stats fields
  adminCount?: number;
  customerCount?: number;
  providerCount?: number;
  orderCount?: number;
  subscriptionStatus?: 'active' | 'trial' | 'expired' | 'none';
  subscriptionExpiresAt?: string;
}

export interface TenantModule {
  id: number;
  moduleKey: string;
  isEnabled: boolean;
  configJson?: string;
  displayOrder: number;
}

export interface TenantModuleChannels {
  customerApp: boolean;
  customerWeb: boolean;
  providerApp: boolean;
  operationsApp: boolean;
  providerAdminPortal: boolean;
  publicPages: boolean;
  marketplace: boolean;
  tenantPortal: boolean;
  operationsPortal: boolean;
}

export interface TenantModuleConfig {
  experienceMode: 'unified_marketplace' | 'direct_only' | 'hybrid';
  operatingModel?: TenantOperatingModel;
  channels: TenantModuleChannels;
}

export type TenantOperatingModel = 'managed_network' | 'marketplace';

export interface TenantChannelOptions {
  customerWeb: boolean;
  customerApp: boolean;
  providerAdminPortal: boolean;
  providerApp: boolean;
  operationsApp: boolean;
  publicPages: boolean;
  tenantPortal: boolean;
  operationsPortal: boolean;
}

export interface TenantNotificationOptions {
  inheritPlatformDefaults: boolean;
  push: boolean;
  email: boolean;
  sms: boolean;
  whatsApp: boolean;
  telegram: boolean;
}

export interface TenantListItem {
  id: number;
  name: string;
  identifier: string;
  currency: string;
  isActive: boolean;
  moduleCount: number;
  createdOn: string;
  // Location info
  country?: string;
  city?: string;
  // Stats
  adminCount?: number;
  customerCount?: number;
  providerCount?: number;
  orderCount?: number;
  // Subscription
  subscriptionStatus?: 'active' | 'trial' | 'expired' | 'none';
  subscriptionExpiresAt?: string;
}

export interface CreateTenantRequest {
  governanceMode?: 'operator_managed' | 'independent_businesses';
  name: string;
  identifier: string;
  timezone?: string;
  currency?: string;
  defaultLocale?: string;
  brandingJson?: string;
  isActive?: boolean;
  enabledModules?: string[];
  operatingModel?: TenantOperatingModel;
  channels?: TenantChannelOptions;
  notificationOptions?: TenantNotificationOptions;
  modules?: TenantModuleInput[];
  adminEmail?: string;
  adminMobile?: string;
  country?: string;
  city?: string;
  phonePrefix?: string;
  countryCode?: string;
  adminPassword?: string;
  /**
   * When set, the new tenant is created with a deep clone of the source
   * tenant's catalog: Categories, SubCategories, Specialties,
   * SubSpecialties (Platform-curated only), SpecialtyDispatchers, and
   * SpecialtyQuestions. Skips the manual catalog data entry that would
   * otherwise take hours per tenant.
   */
  cloneFromTenantId?: number;
}

export interface UpdateTenantRequest {
  name: string;
  timezone?: string;
  currency?: string;
  defaultLocale?: string;
  brandingJson?: string;
  isActive?: boolean;
  /** Phase 3.HH */
  requireCompletionPhoto?: boolean;
  adminEmail?: string;
  adminMobile?: string;
  country?: string;
  city?: string;
  phonePrefix?: string;
  countryCode?: string;
}

export interface SetTenantModulesRequest {
  modules: TenantModuleInput[];
  operatingModel?: TenantOperatingModel;
  channels?: TenantChannelOptions;
}

export interface TenantModuleInput {
  moduleKey: string;
  isEnabled: boolean;
  configJson?: string;
  displayOrder: number;
}

export interface AvailableModule {
  key: string;
  name: string;
}

// Module Keys Constants
export const ModuleKeys = {
  HomeServices: 'home_services',
  Booking: 'booking',
  OnlineServices: 'online_services',
  Shop: 'shop',
} as const;

export type ModuleKey = typeof ModuleKeys[keyof typeof ModuleKeys];

// Current-tenant bootstrap (subset of GET /tenants/meta) used to gate the
// dashboard by the tenant's enabled modules.
export interface CurrentTenantMeta {
  fieldOperationModules?: string[];
  experienceMode?: string;
  operatingModel?: string;
  enabledModules: string[];
  channels?: Record<string, boolean>;
  moduleConfigs: Record<
    string,
    { experienceMode?: string; channels?: Record<string, boolean> }
  >;
}

export interface UpdateAppVersionRequest {
  description?: string;
  isForcedUpdate: boolean;
  updateUrl?: string | null;
}

// Branding Types
export interface PlatformBranding {
  logoUrl?: string;
  splashImageUrl?: string;
  appIconUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  appName?: string;
  appNameArabic?: string;
  tagline?: string;
  taglineArabic?: string;
  termsUrl?: string;
  privacyUrl?: string;
  fontFamily?: string;
  arabicFontFamily?: string;
  fontFileUrl?: string;
  arabicFontFileUrl?: string;
}

export interface TenantBranding {
  website?: PlatformBranding;
  customerApp?: PlatformBranding;
  providerApp?: PlatformBranding;
  operationsApp?: PlatformBranding;
}

// Slider Types
export interface SliderItem {
  id: number;
  imagePath?: string;
  arabicName?: string;
  englishName?: string;
  arabicDescription?: string;
  englishDescription?: string;
  userTypeEnum: number;
  orderPriority: number;
  sliderTypeEnum: number;
  categoryId: number;
  itemId?: string;
  isActive: boolean;
}

export interface CreateSliderRequest {
  imagePath?: string;
  arabicName?: string;
  englishName?: string;
  arabicDescription?: string;
  englishDescription?: string;
  userTypeEnum: number;
  orderPriority: number;
  sliderTypeEnum?: number;
  categoryId?: number;
  itemId?: string;
  isActive?: boolean;
}

export interface UpdateSliderRequest {
  imagePath?: string;
  arabicName?: string;
  englishName?: string;
  arabicDescription?: string;
  englishDescription?: string;
  userTypeEnum: number;
  orderPriority: number;
  isActive: boolean;
}

// Chat Comment for monitoring
export interface ChatComment {
  id: number;
  orderId: number;
  userId: string;
  userName?: string;
  userProfileImage?: string;
  userTypeEnum: UserType;
  messageType: MessageType;
  body: string;
  filePath?: string;
  createdOn: string;
  isHandled: boolean;
  handledNote?: string;
  handledByUserId?: string;
  handledByName?: string;
  handledOn?: string;
}

export interface HandleChatMessageRequest {
  isHandled: boolean;
  handledNote?: string;
}

// FAQ Types
export interface Faq {
  id: number;
  faqTypeId: number;
  arabicTitle: string;
  englishTitle: string;
  arabicHtmlBody: string;
  englishHtmlBody: string;
  isActive: boolean;
  viewCounts: number;
  likesCount: number;
  dislikesCount: number;
  orderPriority: number;
  createdOn: string;
  faqType?: FaqType;
}

export interface FaqType {
  id: number;
  arabicName: string;
  englishName: string;
  forUserType: number;
  isActive: boolean;
}

// Quotation Types
export enum QuotationStatus {
  Pending = 0,
  Approved = 1,
  Closed = 2,
}

export interface Quotation {
  id: number;
  customerUserId: string;
  customerId: number;
  customerName?: string;
  customerMobile?: string;
  customerProfileImage?: string;
  specialtyId: number;
  specialtyName?: string;
  specialtyArabic?: string;
  specialtyEnglish?: string;
  body: string;
  customerLatitude: number;
  customerLongitude: number;
  customerLocationDescription?: string;
  isApproved: boolean;
  approvedOn?: string;
  approvedByUserId?: string;
  approvedByName?: string;
  closedOn?: string;
  closedReason?: string;
  closedByUserId?: string;
  closedByName?: string;
  createdOn: string;
  selectedQuotationOfferId?: number;
  totalPrice?: number;
  offersCount?: number;
}

export interface QuotationOffer {
  id: number;
  quotationId: number;
  providerUserId: string;
  providerId?: number;
  providerName?: string;
  providerProfileImage?: string;
  providerRate?: number;
  price: number;
  note?: string;
  isSelected: boolean;
  createdOn: string;
}

export interface QuotationNote {
  id: number;
  quotationId: number;
  note: string;
  createdByUserId: string;
  createdByName?: string;
  createdOn: string;
}

// Provider Entity Configuration (Center/Shop settings).
// Mirrors backend ProviderProfileInputModel: keyed on the GUID userId with
// an updateType (2=CenterInfo, 3=ShopInfo). The backend writes EVERY field
// of the selected section unconditionally, so untouched values (specialty
// link, logo/cover images) must be echoed back from the loaded provider.
export interface ProviderEntityConfig {
  userId: string;
  updateType:
    | ProviderProfileUpdateType.CenterInfo
    | ProviderProfileUpdateType.ShopInfo;
  // Center fields (updateType 2)
  haveCenter?: boolean;
  centerName?: string;
  centerDescription?: string;
  centerLocationDescription?: string;
  centerLatitude?: number;
  centerLongitude?: number;
  centerOpenAt?: string;
  centerCloseAt?: string;
  centerOpenningDays?: CenterOpeningDaysOption;
  centerSpecailtyId?: number;
  centerLogoImage?: string | null;
  centerCoverImage?: string | null;
  // Shop fields (updateType 3)
  haveStore?: boolean;
  shopName?: string;
  shopDescription?: string;
  shopLocationDescription?: string;
  shopLatitude?: number;
  shopLongitude?: number;
  shopSpecailtyId?: number;
  shopLogoImage?: string | null;
  shopCoverImage?: string | null;
  isShopHaveDelivery?: boolean;
  deliveryPriceNear?: number;
  deliveryPriceFar?: number;
  /** CUX-025 — null clears the free-delivery promo; numeric sets threshold. */
  freeDeliveryThreshold?: number | null;
}

export interface ProviderPersonalProfileInput {
  userId: string;
  updateType: ProviderProfileUpdateType.PersonalProfile;
  fullName: string;
  bio?: string | null;
  genderId: number;
  nationalityId: number;
  providerType: number;
  mobileNo2?: string | null;
  birthDate?: string | null;
  businessName?: string | null;
  businessDescription?: string | null;
}

// Specialty Configuration Enums
export enum SpecialtyTypeEnum {
  Home = 0,
  Center = 1,
  Shop = 2,
  Online = 3,
}

export enum SpecialtyAvailabilityModeEnum {
  Auto = 0,
  LiveLocation = 1,
  Schedule = 2,
  Storefront = 3,
  OnlinePresence = 4,
  ScheduleOrOnline = 5,
}

export enum SpecialtyExecuationModelEnum {
  Quotation = 1,
  CatalogServices = 2,
  ProviderServices = 3,
}

export enum SpecialtyUISelectionEnum {
  QuotationWizard = 1,
  CatalogServicesWizard = 2,
  ProviderServicesList = 3,
  ProvidersList = 4,
}

export enum ProviderSelectionModeEnum {
  Inherit = 0,
  Automatic = 1,
  CustomerChoice = 2,
  Either = 3,
}

export enum TransportationPriceTypeEnum {
  No = 0,
  FixedForAll = 1,
  BasedOnDistrict = 2,
  BasedOnGPSLocation = 3,
  ByEachProvider = 4,
}

export enum CoverageTypeEnum {
  AllInCity = 0,
  BasedOnDistrict = 1,
  BasedOnGPSLocation = 2,
}

export enum SpecialtyDispatcherTypeEnum {
  ByNearDynamicLastGPSLocation = 0,
  ByNearHomeGPSLocation = 1,
  ByTopRated = 2,
  ByBookingTable = 3,
}

// Specialty Dispatcher
export interface SpecialtyDispatcher {
  id: number;
  name: string;
  specialtyDispatcherType: SpecialtyDispatcherTypeEnum;
  searchDistance: number;
  timingLimitUpTo: number;
}

// SubCategory matches Categories.SubCategory entity
export interface SubCategory {
  id: number;
  categoryId: number;
  categoryName?: string;
  arabicName: string;
  englishName: string;
  description?: string;
  imagePath?: string;
  orderPriority: number;
  isActive: boolean;
  tenantId?: number;
}

// SubSpecialty matches Specialties.SubSpecialty entity
export interface SubSpecialty {
  id: number;
  specialtyId: number;
  specialtyName?: string;
  arabicName: string;
  englishName: string;
  isActive: boolean;
  orderPriority: number;
  subSpecialtyBy: SubSpecialtyByEnum;
  providerUserId?: string;
  providerId: number;
  createdOn?: string;
}

export enum SubSpecialtyByEnum {
  Platform = 0,
  Provider = 1,
}

// Request types for CRUD
export interface CreateSubCategoryRequest {
  categoryId: number;
  arabicName: string;
  englishName: string;
  description?: string;
  imagePath?: string;
  orderPriority?: number;
  isActive?: boolean;
}

export interface UpdateSubCategoryRequest extends CreateSubCategoryRequest {
  id: number;
}

export interface CreateSubSpecialtyRequest {
  specialtyId: number;
  arabicName: string;
  englishName: string;
  orderPriority?: number;
  isActive?: boolean;
}

export interface UpdateSubSpecialtyRequest extends CreateSubSpecialtyRequest {
  id: number;
}

export type OrderViewModel = Order;

export interface Violation {
  id: number;
  violationTypeName?: string;
  violationType?: ViolationType;
  penaltyTypeName?: string;
  penaltyType?: PenaltyType;
  note?: string;
  createdByName?: string;
  createdOn: string;
}

export interface Penalty {
  id: number;
  penaltyTypeName?: string;
  penaltyType?: PenaltyType;
  note?: string;
  createdOn: string;
}

// DistrictCoverageAdminViewModel - for coverage management
export interface DistrictCoverage {
  id: number;
  specialtyId: number;
  districtId: number;
  districtName: string;
  cityName: string;
  isCovered: boolean;
}

// TransportationPriceAdminViewModel - for transportation pricing
export interface TransportationPrice {
  id: number;
  specialtyId: number;
  districtId: number;
  districtName: string;
  cityName: string;
  price: number;
}

// CoverageSubscriberAdminViewModel - providers subscribed to coverage
export interface CoverageSubscriber {
  id: number;
  userId: string;
  specialtyId: number;
  districtId: number;
  districtName: string;
  createdOn?: string;
}

// SpecialtyAdminDetailViewModel - Full specialty detail for admin editing
export interface SpecialtyAdminDetail {
  id: number;
  categoryId: number;
  categoryName: string;
  subCategoryId: number;
  arabicName: string;
  englishName: string;
  imagePath?: string;
  serviceQuestionDescription?: string;
  htmlTerms?: string;
  htmlTermsForProviders?: string;
  isActive: boolean;
  orderPriority: number;
  createdOn?: string;
  // Registration Conditions
  requiredOfficeChecking: boolean;
  // Custom Questions
  haveCustomerQuestionsForm: boolean;
  haveProviderQuestionsForm: boolean;
  haveProviderCompletionReportForm: boolean;
  supportsSpareParts?: boolean;
  // Types
  specialtyType: SpecialtyTypeEnum;
  specialtyExecuationModel: SpecialtyExecuationModelEnum;
  availabilityMode: SpecialtyAvailabilityModeEnum;
  specialtySelectionTypeId: SpecialtyUISelectionEnum;
  providerSelectionMode: ProviderSelectionModeEnum;
  // Dispatching
  specialtyDispatcherId: number;
  dispatcherName?: string;
  // Transportation
  transportationPriceType: TransportationPriceTypeEnum;
  fixedTransportationPrice: number;
  gpsBaseTransportationPrice: number;
  transportationPricePerKm: number;
  gpsFreeDistanceKm: number;
  // Coverage
  coverageType: CoverageTypeEnum;
  // Prices
  deepCheckingPrice: number;
  checkingPrice: number;
  guaranteePeriodInDays: number;
  guaranteePrice: number;
  // Commission
  platformCommission: number;
  balanceCap: number;
  // Legacy fields for backward compatibility
  icon?: string;
  categoryArabic?: string;
  categoryEnglish?: string;
  specialtyDispatcher?: SpecialtyDispatcher;
  updatedOn?: string;
}

// Request types for Specialty CRUD
export interface CreateSpecialtyRequest {
  categoryId: number;
  subCategoryId?: number;
  arabicName: string;
  englishName: string;
  imagePath?: string;
  orderPriority?: number;
  isActive?: boolean;
  specialtyType: SpecialtyTypeEnum;
  specialtyExecuationModel: SpecialtyExecuationModelEnum;
  availabilityMode?: SpecialtyAvailabilityModeEnum;
  specialtySelectionTypeId: SpecialtyUISelectionEnum;
  providerSelectionMode?: ProviderSelectionModeEnum;
  serviceQuestionDescription?: string;
  checkingPrice?: number;
  deepCheckingPrice?: number;
  guaranteePrice?: number;
  guaranteePeriodInDays?: number;
  platformCommission?: number;
  balanceCap?: number;
  transportationPriceType?: TransportationPriceTypeEnum;
  fixedTransportationPrice?: number;
  gpsBaseTransportationPrice?: number;
  transportationPricePerKm?: number;
  gpsFreeDistanceKm?: number;
  coverageType?: CoverageTypeEnum;
  specialtyDispatcherId?: number;
  requiredOfficeChecking?: boolean;
  haveCustomerQuestionsForm?: boolean;
  haveProviderQuestionsForm?: boolean;
  haveProviderCompletionReportForm?: boolean;
  supportsSpareParts?: boolean;
  htmlTerms?: string;
  htmlTermsForProviders?: string;
}

export interface UpdateSpecialtyRequest extends CreateSpecialtyRequest {
  id: number;
}

// Dispatcher request types
export interface CreateDispatcherRequest {
  name: string;
  specialtyDispatcherType: SpecialtyDispatcherTypeEnum;
  searchDistance: number;
  timingLimitUpTo: number;
}

export interface UpdateDispatcherRequest extends CreateDispatcherRequest {
  id: number;
}

// Specialty District Coverage - Districts where specialty is available
// Matches backend DistrictCoverageAdminViewModel (the real GET shape).
// "isCovered" replaces the old isActive; districtName/cityName are
// flat strings (the old districtArabicName/cityId shape was wrong).
export interface SpecialtyDistrictCoverage {
  id: number;
  specialtyId: number;
  districtId: number;
  districtName: string;
  cityName: string;
  isCovered: boolean;
}

// Matches backend TransportationPriceAdminViewModel.
export interface SpecialtyDistrictTransportationPrice {
  id: number;
  specialtyId: number;
  districtId: number;
  districtName: string;
  cityName: string;
  price: number;
}

// Specialty District Coverage Subscriber - Providers subscribed to coverage
export interface SpecialtyDistrictCoverageSubscriber {
  id: number;
  userId: string;
  specialtyId: number;
  districtId: number;
  districtName: string;
  createdOn: string;
}

// Catalog Service - Pre-defined services for a specialty (CatalogServices execution model)
export interface CatalogService {
  bookingSettings?: ServiceBookingSettings;
  id: number;
  specialtyId: number;
  specialty?: Specialty;
  subSpecialtyId?: number;
  subSpecialtyName?: string;
  name: string;
  description?: string;
  price: number;
  minimumQuantity: number;
  maximumQuantity: number;
  durationMinutes?: number;
  serviceType: number;
  serviceFor: number;
  providerUserId: string;
  providerId: number;
  imagePath?: string;
  orderPriority: number;
  isActive: boolean;
  addedOn?: string;
}

export interface CreateCatalogServiceRequest {
  bookingSettings?: ServiceBookingSettings;
  specialtyId: number;
  subSpecialtyId?: number;
  name: string;
  description?: string;
  price: number;
  minimumQuantity: number;
  maximumQuantity: number;
  durationMinutes?: number;
  serviceType?: number;
  imagePath?: string;
  orderPriority?: number;
  isActive?: boolean;
}

export interface UpdateCatalogServiceRequest extends CreateCatalogServiceRequest {
  id: number;
}

// Map Types
export interface NearbyOrder {
  id: number;
  orderNumber: string;
  specialtyName?: string;
  status: number;
  statusName?: string;
  latitude: number;
  longitude: number;
  createdOn: string;
}

export interface NearbyProvider {
  id: number;
  userId: string;
  name: string;
  phoneNumber?: string;
  specialtyName?: string;
  isVerified: boolean;
  latitude: number;
  longitude: number;
  rating?: number;
}
